import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BattlePlanSectionResponse, BattlePlanSectionTaskResponse, SectionId, TaskId } from '@business/entities/battle_plan';
import { BattlePlanSectionAPI } from '@business/api/battle_plan_section';
import { map, tap } from 'rxjs/operators';
import { BattlePlanSectionTaskAPI } from '@business/api/battle_plan_section_task';
import { UserFilterFormData } from '@modules/battlePlan/BattlePlanPage/BattlePlanPage';
import { parseMoment, toMoment } from '@core/utils/time';
import { UserId } from '@business/entities/account';
import { TaskStatusId } from '@business/entities/task_status';

export class BattlePlanSectionBloc extends BaseBloc {
  private readonly battlePlanSectionRepo = new Repository<BattlePlanSectionResponse>({
    getItemId: item => item.id,
  });

  readonly battlePlanSections$ = this.battlePlanSectionRepo.items$;
  readonly selectSection = this.battlePlanSectionRepo.selectItem;

  readonly completedBPSections$ = this.battlePlanSections$.pipe(
    map(items =>
      items.map(item => {
        return {
          ...item,
          tasks: item.tasks.filter(
            u => u.isTaskCompleted && u.completedAt && !parseMoment(u.completedAt, 'YYYY-MM-DD HH:mm:ss').isSame(toMoment(), 'day'),
          ),
        };
      }),
    ),
  );

  readonly notCompletedBPSections$ = this.battlePlanSections$.pipe(
    map(items =>
      items.map(item => {
        return {
          ...item,
          tasks: item.tasks.filter(u => parseMoment(u.completedAt ?? '', 'YYYY-MM-DD HH:mm:ss').isSame(toMoment(), 'day') || !u.isTaskCompleted),
        };
      }),
    ),
  );

  onReset() {
    this.battlePlanSectionRepo.reset();
  }

  fetchBattlePlanSections = (param?: UserFilterFormData) => {
    return BattlePlanSectionAPI.fetchBattlePlanSection(param).pipe(
      this.battlePlanSectionRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_battle_plan_section_error') }),
    );
  };

  createBattlePlanSection = (data: Partial<BattlePlanSectionResponse>) => {
    return BattlePlanSectionAPI.createBattlePlanSection(data).pipe(
      this.battlePlanSectionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_battle_plan_section'),
      }),
    );
  };

  selectOneSection = (sectionId: SectionId) => {
    return BattlePlanSectionAPI.selectOneSection(sectionId).pipe(
      this.battlePlanSectionRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_battle_plan_section_error') }),
    );
  };

  deleteBattlePlanSection = (id: SectionId) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => BattlePlanSectionAPI.deleteBattlePlanSection(id)),
      this.battlePlanSectionRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_battle_plan_section'),
      }),
    );
  };

  updateBattlePlanSection = (id: SectionId, data: Partial<BattlePlanSectionResponse>) => {
    return BattlePlanSectionAPI.updateBattlePlanSection(id, data).pipe(
      tap(() => {
        const currentSection = this.battlePlanSectionRepo.getItem(id)!;
        const section = { ...currentSection, ...data };
        this.battlePlanSectionRepo.updateOne({
          id: id,
          changes: () => ({ ...section }),
        });
      }),
      handleMessage({
        type: requestMessage('update_battle_plan_section'),
      }),
    );
  };

  sortBattlePlanSection = (section: BattlePlanSectionResponse, fromPosition: any, toPosition: number) => {
    return BattlePlanSectionAPI.sortBattlePlanSection(section, fromPosition, toPosition).pipe(
      this.battlePlanSectionRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_battle_plan_section'),
      }),
    );
  };

  markBattlePlanTasksCompleted = (isCompleted: boolean, taskId: TaskId, userId: UserId, sectionId: SectionId) => {
    return BattlePlanSectionTaskAPI.markBattlePlanTasksCompleted(isCompleted, taskId, userId).pipe(
      //   tap(() => {
      //     const section = this.battlePlanSectionRepo.getItem(sectionId)!;
      //     const tasks = section.tasks.map(task => {
      //       if (task.id === taskId) {
      //         return { ...task, isTaskCompleted: isCompleted };
      //       }
      //       return task;
      //     });
      //     this.battlePlanSectionRepo.updateOne({
      //       id: sectionId,
      //       changes: () => ({ tasks: tasks }),
      //     });
      //   }),
      handleMessage({
        type: requestMessage('complete_battle_plan_task'),
      }),
    );
  };

  sortBattlePlanTasks = (sectionId: SectionId, taskIds: TaskId[]) => {
    return BattlePlanSectionTaskAPI.sortBattlePlanTasks(sectionId, taskIds).pipe(
      //   this.battlePlanSectionTasksRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_battle_plan_task'),
      }),
    );
  };

  sortSectionTasks = (payload: any[], userId: UserId) => {
    return BattlePlanSectionTaskAPI.sortSectionTasks(payload, userId).pipe(
      //   this.battlePlanSectionTasksRepo.ops.upsertMany(item => ({ item })),
      //   handleMessage({
      //     type: requestMessage('update_battle_plan_task'),
      //   }),
      this.battlePlanSectionRepo.ops.reset(),
    );
  };

  deleteBattlePlanTask = (id: TaskId, sectionId: SectionId, userId: UserId) => {
    return showDeleteConfirmation('Delete Task', 'Do you really want to remove this Task?').pipe(
      switchMap(() => BattlePlanSectionTaskAPI.deleteBattlePlanTasks(id, userId)),
      //   tap(() => {
      //     const section = this.battlePlanSectionRepo.getItem(sectionId == '0' ? '-1' : sectionId)!;
      //     const tasks = section.tasks.filter(task => task.id !== id);
      //     this.battlePlanSectionRepo.updateOne({
      //       id: sectionId,
      //       changes: () => ({ tasks }),
      //     });
      //   }),
      this.battlePlanSectionRepo.ops.reset(),
      handleMessage({
        type: requestMessage('delete_battle_plan_task'),
      }),
    );
  };

  createBattlePlanTask = (data: Partial<BattlePlanSectionTaskResponse>) => {
    return BattlePlanSectionTaskAPI.createBattlePlanTasks(data).pipe(
      tap(data => {
        const section = this.battlePlanSectionRepo.getItem(data.sectionId == '0' ? '-1' : data.sectionId)!;
        const tasks = section.tasks.concat(data);
        this.battlePlanSectionRepo.updateOne({
          // id: data.sectionId,
          id: data.sectionId == '0' ? '-1' : data.sectionId,
          changes: () => ({ tasks }),
        });
      }),
      handleMessage({
        type: requestMessage('create_battle_plan_task'),
      }),
    );
  };

  updateBattlePlanTask = (id: TaskId, data: Partial<BattlePlanSectionTaskResponse>) => {
    return BattlePlanSectionTaskAPI.updateBattlePlanTasks(id, data).pipe(
      tap(data => {
        const section = this.battlePlanSectionRepo.getItem(data.sectionId == '0' ? '-1' : data.sectionId)!;
        const tasks = section.tasks.map(task => {
          if (task.id === id) {
            return { ...task, ...data };
          }
          return task;
        });
        this.battlePlanSectionRepo.updateOne({
          id: data.sectionId,
          changes: () => ({ tasks }),
        });
      }),
      handleMessage({
        type: requestMessage('update_battle_plan_task'),
      }),
    );
  };

  updateStepStatus = (id: TaskId, statusId: TaskStatusId | undefined) => {
    return BattlePlanSectionTaskAPI.updateStepStatus(id, statusId).pipe(
      tap(data => {
        const section = this.battlePlanSectionRepo.getItem(data.sectionId == '0' ? '-1' : data.sectionId)!;
        const tasks = section.tasks.map(task => {
          if (task.id === id) {
            return { ...task, ...data };
          }
          return task;
        });
        this.battlePlanSectionRepo.updateOne({
          id: data.sectionId,
          changes: () => ({ tasks }),
        });
      }),
      handleMessage({
        type: requestMessage('update_assignee_steps'),
      }),
    );
  };

  updateStepUsers = (id: TaskId, userIds: UserId[]) => {
    return BattlePlanSectionTaskAPI.updateAssignedusers(id, userIds).pipe(
      tap(data => {
        const section = this.battlePlanSectionRepo.getItem(data.sectionId == '0' ? '-1' : data.sectionId)!;
        const tasks = section.tasks.map(task => {
          if (task.id === id) {
            return { ...task, ...data };
          }
          return task;
        });
        this.battlePlanSectionRepo.updateOne({
          id: data.sectionId,
          changes: () => ({ tasks }),
        });
      }),
      handleMessage({
        type: requestMessage('update_assignee_steps'),
      }),
    );
  };

  fetchUsers = () => {
    return BattlePlanSectionAPI.fetchUsers().pipe(handleMessage({ error: requestMessage('fetch_users_error') }));
  };
}
